/**
 * Copyright 2023 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import { CMSCertona } from '@/features/contentstack/components/CMSCertona/CMSCertona';
import type {
  ContentStackMerchandisingShelfPage,
  ContentStackProductListingPage,
  ContentStackShelfPage,
} from '@/features/contentstack/interface';
import React from 'react';
import {
  type CertonaBlockProps,
  type CertonaRecommendation,
  ScrolledPartCardSkeleton,
} from '@/features/certona';
import { CMSShelfSpacer } from '@/features/contentstack/components/CMSShelfSpacer/CMSShelfSpacer';
import { useProductList } from '../../../context/useProductList';
import { useLocale } from '@/hooks/useLocale';
import { countryCodes } from '@/constants/locale';
import { FeatureFlag } from '@/features/globalConfig';
import { DynamicLinks } from '@/features/dlm/components/DynamicLinks';
import { VehicleDynamicLinks } from '@/features/dlm/components/VehicleDynamicLinks';
import { CMSPLPSeoCopy } from '@/features/productListingPage/components/CMSPLPSeoCopy/CMSPLPSeoCopy';
import { CMSMakeModelYearRange } from '@/features/contentstack/components/CMSMakeModelYearRange/CMSMakeModelYearRange';
import { OptionalSuspense } from '@/features/performance/components/OptionalSuspense';
import { useCertonaAddToCartFlag } from '@/hooks/useCertonaAddToCartCTAFlag';
import { CMSFaq } from '@/features/contentstack/components/CMSFaq/CMSFaq';
import { CMSProductTypeGrid } from '@/features/contentstack/components/CMSProductTypeGrid/CMSProductTypeGrid';
import { CMSCategoryGrid } from '@/features/contentstack/components/CMSCategoryGrid/CMSCategoryGrid';
import { CMSProductChart } from '@/features/contentstack/components/CMSProductChart/CMSProductChart';
import { CMSImageCategory } from '@/features/contentstack/components/CMSImageCategory/CMSImageCategory';
import { CMSBlogGrid } from '@/features/contentstack/components/CMSBlogGrid/CMSBlogGrid';
import { getCMSModularBlockId } from '@/features/contentstack/utils/getCMSModularBlockId';
import { type SeoFaqsData } from '@/features/common';
import { getBaseSchemaUrl } from '@/utils/common';

type Props = {
  certonaRecommendations: CertonaRecommendation[];
  content:
    | NonNullable<ContentStackShelfPage['shelfresultbottom']>
    | NonNullable<ContentStackMerchandisingShelfPage['shelfresultbottom']>
    | Array<NonNullable<ContentStackProductListingPage['shelfresultbottom']>[0]>;
  faqsData?: SeoFaqsData | null;
} & CertonaBlockProps;

const CMSResultBottom = ({
  certonaRecommendations,
  content,
  onCertonaRecommendationClick,
  setAddedCertonaItemObject,
  onCertonaAddToCartClick,
  faqsData,
}: Props) => {
  const { data: shelfData } = useProductList();
  const shelfProductId = shelfData?.productId;
  const locale = useLocale();
  const makeModelYearPageType = shelfData?.makeModelYearPageType;

  const isShelfCertonaAddToCartEnabled = useCertonaAddToCartFlag();

  return (
    <>
      {content.map((modularBlock, index) => {
        const getBlockId = (blockName: string) => getCMSModularBlockId(modularBlock, blockName);
        if ('faq' in modularBlock && modularBlock.faq?.faq?.[0]) {
          return (
            <OptionalSuspense key={index}>
              <CMSShelfSpacer id={getBlockId('faq')}>
                <CMSFaq content={modularBlock.faq.faq[0]} />
              </CMSShelfSpacer>
            </OptionalSuspense>
          );
        }
        const containerID = modularBlock.certona?.certona?.[0]?.container_id;
        const ignoredContainerIDs = [
          'accessoriesshelf1_rr',
          'partshelf1_rr',
          'accessoriesshelf_rr',
          'partshelf_rr',
        ];
        const shouldIgnoreSpacing = Boolean(
          containerID && ignoredContainerIDs.includes(containerID),
        );
        if ('seo_copy_content' in modularBlock && modularBlock.seo_copy_content) {
          return (
            <OptionalSuspense key={index}>
              <CMSShelfSpacer id={getBlockId('seo_copy_content')}>
                <CMSPLPSeoCopy />
              </CMSShelfSpacer>
              {faqsData?.faqs?.length ? (
                <CMSShelfSpacer>
                  <CMSFaq
                    content={{
                      title: faqsData.title,
                      heading: faqsData.heading,
                      faq: faqsData.faqs,
                      theme: 'Accordion View',
                    }}
                    pageURL={faqsData.url ? getBaseSchemaUrl(locale) + faqsData.url : undefined}
                  />
                </CMSShelfSpacer>
              ) : null}
            </OptionalSuspense>
          );
        }

        if (modularBlock.certona?.certona?.[0]) {
          return (
            <OptionalSuspense key={index}>
              <CMSShelfSpacer
                shouldIgnoreCarouselSpacing={shouldIgnoreSpacing}
                id={getBlockId('certona')}
              >
                <CMSCertona
                  content={modularBlock.certona.certona[0]}
                  certonaRecommendations={certonaRecommendations}
                  onCertonaRecommendationClick={onCertonaRecommendationClick}
                  onCertonaAddToCartClick={onCertonaAddToCartClick}
                  setAddedCertonaItemObject={setAddedCertonaItemObject}
                  onlyShowAvailableProductRecommendations={isShelfCertonaAddToCartEnabled}
                  //TO-DO update loading state for mobile once they are added in this https://track.autozone.com/browse/SEB-11317
                  LoadingSkeleton={ScrolledPartCardSkeleton}
                  subtitle={modularBlock.certona.certona[0].subtitle}
                />
              </CMSShelfSpacer>
            </OptionalSuspense>
          );
        }

        if (modularBlock.pagination_style_links?.enable) {
          return (
            <OptionalSuspense key={index}>
              <div id={getBlockId('pagination_style_links')}>
                {shelfProductId && locale === countryCodes.us && !makeModelYearPageType && (
                  <FeatureFlag flag="DLM_SHELF_ENABLED">
                    <CMSShelfSpacer>
                      <DynamicLinks seoRepositoryId={shelfProductId} />
                    </CMSShelfSpacer>
                  </FeatureFlag>
                )}
                {shelfProductId && locale === countryCodes.us && makeModelYearPageType && (
                  <FeatureFlag flag="DLM_YMM_SHELF_ENABLED">
                    <CMSShelfSpacer>
                      <VehicleDynamicLinks seoRepositoryId={shelfProductId} />
                    </CMSShelfSpacer>
                  </FeatureFlag>
                )}
              </div>
            </OptionalSuspense>
          );
        }

        if (modularBlock.year_range_for_makemodel_by_model?.enable) {
          return (
            <OptionalSuspense key={index}>
              <CMSShelfSpacer id={getBlockId('year_range_for_makemodel_by_model')}>
                <CMSMakeModelYearRange />
              </CMSShelfSpacer>
            </OptionalSuspense>
          );
        }

        if ('product_grid' in modularBlock && modularBlock.product_grid?.product_grid?.[0]) {
          return (
            <CMSShelfSpacer key={index} id={getBlockId('product_grid')}>
              <CMSProductTypeGrid
                content={modularBlock.product_grid.product_grid[0]}
                gridIndex={index}
              />
            </CMSShelfSpacer>
          );
        }

        if ('blogtype_grid' in modularBlock && modularBlock.blogtype_grid?.blogtype_grid?.[0]) {
          return (
            <CMSShelfSpacer key={index} id={getBlockId('blogtype_grid')}>
              <CMSBlogGrid content={modularBlock.blogtype_grid.blogtype_grid[0]} />
            </CMSShelfSpacer>
          );
        }

        if ('category_grid' in modularBlock && modularBlock.category_grid?.category_grid?.[0]) {
          return (
            <CMSShelfSpacer key={index} id={getBlockId('category_grid')}>
              <CMSCategoryGrid content={modularBlock.category_grid.category_grid[0]} />
            </CMSShelfSpacer>
          );
        }

        if ('product_chart' in modularBlock && modularBlock.product_chart?.product_chart?.[0]) {
          return (
            <CMSShelfSpacer key={index} id={getBlockId('product_chart')}>
              <CMSProductChart content={modularBlock.product_chart.product_chart[0]} />
            </CMSShelfSpacer>
          );
        }

        if (
          'image_category_grid' in modularBlock &&
          modularBlock.image_category_grid?.image_category_grid?.[0]
        ) {
          return (
            <CMSShelfSpacer key={index} id={getBlockId('image_category_grid')}>
              <CMSImageCategory content={modularBlock.image_category_grid.image_category_grid[0]} />
            </CMSShelfSpacer>
          );
        }

        return null;
      })}
    </>
  );
};

export { CMSResultBottom };
